import React, { useEffect, useState } from "react";
import {
  Navigation as NavIcon,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  RefreshCw,
} from "lucide-react";
import TenantPreferencesService, {
  NavbarLink,
} from "../../_services/TenantPreferencesService";

interface Props {
  onError: (msg: string | null) => void;
  onSuccess: (msg: string | null) => void;
}

const MAX_LINKS = 12;

// Must stay in sync with iconMap in ilearn src/_components/navbar/navbar.jsx —
// only these FontAwesome icons can actually render in the navbar.
const ICON_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "No icon" },
  { value: "faLayerGroup", label: "Layers (catalogue)" },
  { value: "faHeart", label: "Heart" },
  { value: "faUser", label: "User" },
];

const URL_RE = /^(https?:\/\/|mailto:|tel:|\/)/i;

const blankLink = (): NavbarLink => ({ name: "", link: "", icon: "", newTab: false });

/**
 * Navigation tab — per-tenant ilearn top-navbar links stored in the S3
 * app-properties.json `navbar.navbarLinks` array via GET/PUT
 * /v1/tenant-config/navbar. Lets an admin add links (e.g. "Courses" →
 * /Products) that logged-in students see in the ilearn navbar, without
 * hand-editing the tenant's config JSON.
 */
const NavigationTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [links, setLinks] = useState<NavbarLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    onError(null);
    try {
      const rows = await TenantPreferencesService.getNavbarLinks();
      setLinks(rows.map((r) => ({ ...blankLink(), ...r })));
    } catch (e: any) {
      onError(e?.response?.data?.error?.message ?? "Failed to load navigation links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (idx: number, patch: Partial<NavbarLink>) =>
    setLinks((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const addLink = () =>
    setLinks((prev) => (prev.length >= MAX_LINKS ? prev : [...prev, blankLink()]));

  const removeLink = (idx: number) =>
    setLinks((prev) => prev.filter((_, i) => i !== idx));

  const move = (idx: number, dir: -1 | 1) =>
    setLinks((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const handleSave = async () => {
    // Client-side validation mirrors the server so errors are immediate.
    const cleaned: NavbarLink[] = [];
    for (const l of links) {
      const name = (l.name || "").trim();
      const link = (l.link || "").trim();
      if (!name && !link) continue; // skip fully-empty rows
      if (!name || !link) {
        onError("Every link needs both a label and a URL/path.");
        return;
      }
      if (!URL_RE.test(link)) {
        onError(`"${name}": link must start with http(s)://, mailto:, tel:, or / (e.g. /Products).`);
        return;
      }
      cleaned.push({ name, link, icon: l.icon || "", newTab: !!l.newTab });
    }

    setSaving(true);
    onError(null);
    onSuccess(null);
    try {
      const saved = await TenantPreferencesService.saveNavbarLinks(cleaned);
      setLinks(saved.map((r) => ({ ...blankLink(), ...r })));
      onSuccess(
        cleaned.length === 0
          ? "Navigation links cleared. Students will see no custom navbar links."
          : `Saved ${cleaned.length} navigation link${cleaned.length === 1 ? "" : "s"}. Students see the change on their next page load.`,
      );
    } catch (e: any) {
      onError(e?.response?.data?.error?.message ?? "Failed to save navigation links");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="tp-loading">Loading navigation links…</div>;

  return (
    <div className="tp-card">
      <h3>
        <NavIcon size={16} style={{ marginRight: 8, verticalAlign: "-2px" }} />
        Navbar links
      </h3>
      <p className="tp-card-desc">
        Links shown in the ilearn top navbar for signed-in students. Use a
        site-relative path for in-app pages (e.g. <code>/Products</code> for the
        course catalogue) or a full <code>https://</code> URL for an external
        site. Up to {MAX_LINKS}.
      </p>

      {links.length === 0 && (
        <div className="tp-empty" style={{ padding: "12px 0", opacity: 0.75 }}>
          No navbar links yet. Add one below — for example{" "}
          <strong>Courses</strong> → <code>/Products</code>.
        </div>
      )}

      {links.map((l, i) => (
        <div
          key={i}
          className="tp-nav-link-row"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.4fr 160px auto auto",
            gap: 8,
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <input
            className="tp-field-input"
            type="text"
            maxLength={40}
            placeholder="Label (e.g. Courses)"
            value={l.name}
            onChange={(e) => update(i, { name: e.target.value })}
          />
          <input
            className="tp-field-input"
            type="text"
            maxLength={500}
            placeholder="/Products or https://…"
            value={l.link}
            onChange={(e) => update(i, { link: e.target.value })}
          />
          <select
            className="tp-field-input"
            value={l.icon || ""}
            onChange={(e) => update(i, { icon: e.target.value })}
          >
            {ICON_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <label
            style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", fontSize: 13 }}
            title="Open this link in a new browser tab"
          >
            <input
              type="checkbox"
              checked={!!l.newTab}
              onChange={(e) => update(i, { newTab: e.target.checked })}
            />
            New tab
          </label>
          <div style={{ display: "inline-flex", gap: 2 }}>
            <button
              type="button"
              className="tp-btn tp-btn-secondary"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              title="Move up"
              aria-label="Move up"
            >
              <ArrowUp size={14} />
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-secondary"
              onClick={() => move(i, 1)}
              disabled={i === links.length - 1}
              title="Move down"
              aria-label="Move down"
            >
              <ArrowDown size={14} />
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-danger-text"
              onClick={() => removeLink(i)}
              title="Remove link"
              aria-label="Remove link"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}

      <div className="tp-slot-actions" style={{ marginTop: links.length ? 12 : 0 }}>
        <button
          type="button"
          className="tp-btn tp-btn-primary"
          onClick={addLink}
          disabled={links.length >= MAX_LINKS}
        >
          <Plus size={14} /> Add link
        </button>
      </div>

      <div className="tp-slot-actions" style={{ marginTop: 20, display: "flex", gap: 8 }}>
        <button
          type="button"
          className="tp-btn tp-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-secondary"
          onClick={() => void load()}
          disabled={saving}
        >
          <RefreshCw size={14} /> Reset
        </button>
      </div>
    </div>
  );
};

export default NavigationTab;
